// modules/reporteLibroVentas/pages/ReporteLibroVentasPage.jsx
// Migracion de reporte_libro_ventas.php (PHP legado) — libro de ventas IVA:
// mismo cruce Hippo + BD local por CUF que Reporte de Impuestos, pero con
// las columnas fiscales del libro de ventas.
//
// El PHP original leia un filtro de sucursal del formulario pero nunca lo
// aplicaba a las filas (solo mostraba las 3 sub-sumas fijas de siempre) —
// se omite ese selector acá en vez de replicar un filtro que no filtraba.
//
// "Débito Fiscal IVA" queda en 0: se confirmó en vivo que la API de Hippo
// no devuelve ese campo (ni en el listado ni en el detalle por CUF) — el
// reporte viejo tampoco lo tenía realmente disponible.
import React, { useMemo, useState } from "react";
import {
  Box, Button, TextField, Typography, Alert, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  TableSortLabel, Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { getReporteLibroVentas } from "../services/reporteLibroVentasService";

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.72rem",
  whiteSpace: "nowrap",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const celdaTruncada = { maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const haceUnMesISO = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 10);
};

// Coma para miles, punto para decimales — mismo formato que number_format()
// de PHP (usado tal cual en todos los reportes legados que se migraron).
const formatMoneda = (value) =>
  Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatFechaHora = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const getValorOrdenable = (row, col) => {
  if (col === "fechaEmision") return row.fechaEmision ? new Date(row.fechaEmision).getTime() : 0;
  return row[col];
};

const compararValores = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === "string" || typeof b === "string") {
    return String(a).localeCompare(String(b), "es", { numeric: true, sensitivity: "base" });
  }
  return a - b;
};

const SortableHeaderCell = ({ col, label, align, orderBy, orderDir, onSort }) => (
  <StyledHeaderCell align={align} sortDirection={orderBy === col ? orderDir : false}>
    <TableSortLabel
      active={orderBy === col}
      direction={orderBy === col ? orderDir : "asc"}
      onClick={() => onSort(col)}
    >
      {label}
    </TableSortLabel>
  </StyledHeaderCell>
);

const COLUMNAS = [
  { col: "cod", label: "Cod" },
  { col: "fechaEmision", label: "Fecha emisión" },
  { col: "numeroFactura", label: "N° factura" },
  { col: "estado", label: "Estado" },
  { col: "nit", label: "NIT" },
  { col: "nombreFactura", label: "Nombre Factura" },
  { col: "importeTotal", label: "Importe Total", align: "right" },
  { col: "importeIceIehdTasas", label: "ICE/IEHD/TASAS", align: "right" },
  { col: "exportacionesOpExento", label: "Exp. y op. exento", align: "right" },
  { col: "ventasGravadasTasaCero", label: "Ventas Tasa 0", align: "right" },
  { col: "subTotal", label: "Sub Total", align: "right" },
  { col: "descuentosBonificaciones", label: "Desc./Bonif./Rebaja", align: "right" },
  { col: "importeBaseDF", label: "Importe Base DF", align: "right" },
  { col: "debitoFiscalIva", label: "Débito Fiscal IVA", align: "right" },
  { col: "cuf", label: "CUF" },
];

const ReporteLibroVentasPage = () => {
  const [fechaIni, setFechaIni] = useState(haceUnMesISO);
  const [fechaFin, setFechaFin] = useState(hoyISO);
  const [data, setData] = useState(null);
  const [buscado, setBuscado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [orderBy, setOrderBy] = useState("fechaEmision");
  const [orderDir, setOrderDir] = useState("asc");

  const handleBuscar = async () => {
    setLoading(true);
    setError("");
    try {
      const { data: resp } = await getReporteLibroVentas({ fechaIni, fechaFin });
      setData(resp);
      setBuscado(true);
    } catch (e) {
      setData(null);
      setError(e?.response?.data?.error || "No se pudo generar el reporte.");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrderDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(col);
      setOrderDir("asc");
    }
  };

  const itemsOrdenados = useMemo(() => {
    const items = data?.items || [];
    if (!orderBy) return items;
    const copia = [...items].sort((a, b) =>
      compararValores(getValorOrdenable(a, orderBy), getValorOrdenable(b, orderBy))
    );
    if (orderDir === "desc") copia.reverse();
    return copia;
  }, [data, orderBy, orderDir]);

  return (
    <DashboardLayout>
      <PageHeader
        title="Libro de Ventas"
        subtitle="Facturas emitidas al SIAT (vía Hippo) — formato libro de ventas IVA"
        backTo="/reporte-facturas"
      />
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
          <TextField
            type="date" size="small" label="Fecha inicio"
            sx={{ ...compactFieldSx, width: 165 }}
            value={fechaIni}
            onChange={(e) => setFechaIni(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date" size="small" label="Fecha fin"
            sx={{ ...compactFieldSx, width: 165 }}
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            size="small"
            variant="contained"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
            onClick={handleBuscar}
            disabled={loading}
            sx={{ minHeight: 32 }}
          >
            {loading ? "Buscando..." : "Buscar"}
          </Button>
          {buscado && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              Total: {data?.items?.length || 0}
            </Typography>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!buscado ? (
          <Alert severity="info">Elige un rango de fechas y presiona Buscar.</Alert>
        ) : !loading && (data?.items?.length || 0) === 0 ? (
          <Alert severity="info">No se encontraron facturas para el rango seleccionado.</Alert>
        ) : (
          <>
            <Paper variant="outlined">
              <TableContainer sx={{ maxHeight: "60vh" }}>
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    "& td": { py: 0.35, px: 0.75, fontSize: "0.74rem", whiteSpace: "nowrap" },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {COLUMNAS.map((c) => (
                        <SortableHeaderCell key={c.col} {...c} orderBy={orderBy} orderDir={orderDir} onSort={handleSort} />
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={COLUMNAS.length} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : (
                      itemsOrdenados.map((r) => (
                        <TableRow key={r.cuf || r.numeroFactura} hover>
                          <TableCell>{r.cod ?? "-"}</TableCell>
                          <TableCell>{formatFechaHora(r.fechaEmision)}</TableCell>
                          <TableCell>{r.numeroFactura}</TableCell>
                          <TableCell>{r.estado || "-"}</TableCell>
                          <TableCell>{r.nit || "-"}</TableCell>
                          <TableCell sx={celdaTruncada} title={r.nombreFactura}>{r.nombreFactura || "-"}</TableCell>
                          <TableCell align="right">{formatMoneda(r.importeTotal)}</TableCell>
                          <TableCell align="right">{formatMoneda(r.importeIceIehdTasas)}</TableCell>
                          <TableCell align="right">{formatMoneda(r.exportacionesOpExento)}</TableCell>
                          <TableCell align="right">{formatMoneda(r.ventasGravadasTasaCero)}</TableCell>
                          <TableCell align="right">{formatMoneda(r.subTotal)}</TableCell>
                          <TableCell align="right">{formatMoneda(r.descuentosBonificaciones)}</TableCell>
                          <TableCell align="right">{formatMoneda(r.importeBaseDF)}</TableCell>
                          <TableCell align="right">{formatMoneda(r.debitoFiscalIva)}</TableCell>
                          <TableCell sx={{ ...celdaTruncada, maxWidth: 130 }} title={r.cuf}>{r.cuf}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              <Paper variant="outlined" sx={{ p: 1.5, minWidth: 160 }}>
                <Typography variant="caption" color="text.secondary">La Paz</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>Bs. {formatMoneda(data.totales.sumaLPZ)}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5, minWidth: 160 }}>
                <Typography variant="caption" color="text.secondary">Cochabamba</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>Bs. {formatMoneda(data.totales.sumaCBBA)}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5, minWidth: 160 }}>
                <Typography variant="caption" color="text.secondary">Santa Cruz</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>Bs. {formatMoneda(data.totales.sumaSCZ)}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5, minWidth: 200 }}>
                <Typography variant="caption" color="text.secondary">Total en Bolivianos</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Bs. {formatMoneda(data.totales.sumaTotal)}</Typography>
              </Paper>
            </Stack>
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ReporteLibroVentasPage;
